import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { artworkId, imageId } from "../../lib/ids";
import type { frontend as fr } from "../../client";

type Status = "available" | "sold" | "archived";

const STATUSES: Status[] = ["available", "sold", "archived"];

const MAX_IMAGES = 10;

interface StagedImage {
  key: string; // stable id for React, generated client-side
  file: File;
  previewUrl: string;
}

const newStagedImage = (file: File): StagedImage => ({
  key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
  file,
  previewUrl: URL.createObjectURL(file),
});

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const uploadOne = async (artId: string, file: File): Promise<fr.UploadImageResponse> => {
  const data = await fileToBase64(file);
  return apiClient.frontend.UploadImage(artId, {
    ArtworkID: artId as unknown as fr.UploadImageRequest["ArtworkID"],
    ContentType: file.type,
    DataBase64: data,
  });
};

const AdminArtworkEditorPage = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const artworkQuery = useQuery({
    queryKey: ["admin-artwork", id],
    queryFn: () => apiClient.frontend.AdminGetArtwork(id!),
    enabled: !isNew,
  });

  const existing = artworkQuery.data?.Artwork;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceDollars, setPriceDollars] = useState("80");
  const [status, setStatus] = useState<Status>("available");
  const [sortOrder, setSortOrder] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedImage[]>([]);

  // Hydrate the form once the artwork loads.
  useEffect(() => {
    if (!existing) return;
    setTitle(existing.Title);
    setDescription(existing.Description);
    setPriceDollars((existing.PriceCents / 100).toString());
    setStatus(existing.Status as Status);
    setSortOrder(existing.SortOrder);
  }, [existing]);

  // Revoke all preview URLs when the component unmounts.
  useEffect(() => {
    return () => {
      staged.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceCents = useMemo(() => {
    const num = Number.parseFloat(priceDollars);
    if (Number.isNaN(num) || num < 0) return 0;
    return Math.round(num * 100);
  }, [priceDollars]);

  const addStagedFiles = (files: FileList) => {
    setStaged((cur) => {
      const room = MAX_IMAGES - cur.length;
      if (room <= 0) {
        setUploadError(`Max ${MAX_IMAGES} images per artwork.`);
        return cur;
      }
      const incoming: StagedImage[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        incoming.push(newStagedImage(file));
      }
      if (incoming.length > room) {
        // Drop the overflow on the floor and revoke its preview URL so we
        // don't leak.
        const accepted = incoming.slice(0, room);
        incoming.slice(room).forEach((s) => URL.revokeObjectURL(s.previewUrl));
        setUploadError(`Only ${room} image${room === 1 ? "" : "s"} added — max is ${MAX_IMAGES}.`);
        return [...cur, ...accepted];
      }
      setUploadError(null);
      return [...cur, ...incoming];
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeStaged = (key: string) => {
    setStaged((cur) => {
      const next = cur.filter((s) => s.key !== key);
      const removed = cur.find((s) => s.key === key);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  // ---- mutations ----

  const createMutation = useMutation({
    mutationFn: async () => {
      // 1. Create the artwork row.
      const res = await apiClient.frontend.CreateArtwork({
        Title: title,
        Description: description,
        PriceCents: priceCents,
        Status: status,
      });
      const newId = artworkId(res.Artwork.ID);

      // 2. Upload any images that were staged in the browser.
      for (const s of staged) {
        try {
          await uploadOne(newId, s.file);
        } catch (err) {
          setUploadError(
            `Failed to upload "${s.file.name}": ${err instanceof Error ? err.message : "unknown error"}`,
          );
          // We still navigate to the edit page so the user can retry the
          // remaining uploads manually instead of starting over.
          break;
        }
      }
      return res;
    },
    onSuccess: (res) => {
      // Release preview URLs now that uploads have happened (or been attempted).
      staged.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      setStaged([]);
      queryClient.invalidateQueries({ queryKey: ["admin-artworks"] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
      navigate(`/admin/artwork/${artworkId(res.Artwork.ID)}`, { replace: true });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      apiClient.frontend.UpdateArtwork(id!, {
        Title: title,
        Description: description,
        PriceCents: priceCents,
        Status: status,
        SortOrder: sortOrder,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-artworks"] });
      queryClient.invalidateQueries({ queryKey: ["admin-artwork", id] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiClient.frontend.UpdateArtwork(id!, {
        Title: title,
        Description: description,
        PriceCents: priceCents,
        Status: "archived",
        SortOrder: sortOrder,
      }),
    onSuccess: () => {
      setStatus("archived");
      queryClient.invalidateQueries({ queryKey: ["admin-artworks"] });
      queryClient.invalidateQueries({ queryKey: ["admin-artwork", id] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
    },
  });

  const editUploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const currentCount = existing?.Images.length ?? 0;
      const room = MAX_IMAGES - currentCount;
      const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
      if (room <= 0) {
        throw new Error(`Max ${MAX_IMAGES} images per artwork.`);
      }
      const toUpload = list.slice(0, room);
      const skipped = list.length - toUpload.length;

      const results: fr.UploadImageResponse[] = [];
      for (const file of toUpload) {
        results.push(await uploadOne(id!, file));
      }
      return { results, skipped };
    },
    onSuccess: ({ skipped }) => {
      if (skipped > 0) {
        setUploadError(`${skipped} image${skipped === 1 ? "" : "s"} skipped — max is ${MAX_IMAGES}.`);
      } else {
        setUploadError(null);
      }
      queryClient.invalidateQueries({ queryKey: ["admin-artwork", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-artworks"] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (err) => {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imgIdStr: string) => apiClient.frontend.DeleteImage(imgIdStr),
    onSuccess: () => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ["admin-artwork", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-artworks"] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
    },
    onError: (err) => {
      setUploadError(err instanceof Error ? err.message : "Could not remove image.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew) createMutation.mutate();
    else updateMutation.mutate();
  };

  const handleArchive = () => {
    if (status === "archived") return;
    if (!confirm("Archive this artwork? It will be hidden from the public site but remain here in the admin.")) {
      return;
    }
    archiveMutation.mutate();
  };

  if (!isNew && artworkQuery.isLoading) {
    return <p className="text-sm text-[var(--color-kj-text-light)]">Loading…</p>;
  }
  if (!isNew && (artworkQuery.isError || !existing)) {
    return (
      <div>
        <p className="text-sm text-[var(--color-kj-danger)] mb-4">
          Could not load this artwork.
        </p>
        <Link to="/admin" className="text-xs uppercase-track underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const saving = createMutation.isPending || updateMutation.isPending;
  const createSubmitLabel = createMutation.isPending
    ? staged.length > 0
      ? `Creating & uploading ${staged.length}…`
      : "Creating…"
    : staged.length > 0
      ? `Create & upload ${staged.length} image${staged.length === 1 ? "" : "s"}`
      : "Create";

  return (
    <div>
      <Link
        to="/admin"
        className="inline-block mb-8 text-xs uppercase-track text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] transition-colors"
      >
        ← Dashboard
      </Link>

      <h1 className="font-display text-3xl uppercase-track mb-8">
        {isNew ? "New Artwork" : existing!.Title}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-2 gap-12"
      >
        <div className="space-y-6">
          <label className="block">
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              Title
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-base transition-colors"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              Description
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="mt-1 w-full bg-transparent border border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none p-3 text-sm resize-y transition-colors"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              Price (USD)
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={priceDollars}
              onChange={(e) => setPriceDollars(e.target.value)}
              className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-base transition-colors"
            />
          </label>

          <div>
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)] block mb-2">
              Status
            </span>
            <div className="inline-flex border border-[var(--color-kj-border)]">
              {STATUSES.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={[
                    "px-4 py-2 text-xs uppercase-track transition-colors cursor-pointer",
                    i > 0 ? "border-l border-[var(--color-kj-border)]" : "",
                    status === s
                      ? "bg-[var(--color-kj-text)] text-[var(--color-kj-bg)]"
                      : "text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)]",
                  ].join(" ")}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[var(--color-kj-text-light)] mt-2">
              {status === "available" && "Visible on the public site as available."}
              {status === "sold" && "Visible on the public site, marked sold."}
              {status === "archived" && "Hidden from the public site. Only visible here."}
            </p>
          </div>

          {!isNew && (
            <label className="block">
              <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
                Sort order (higher = sooner)
              </span>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-sm transition-colors"
              />
            </label>
          )}

          <div className="flex items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={saving || (isNew && staged.length === 0)}
              title={isNew && staged.length === 0 ? "Add at least one image before creating." : undefined}
              className="font-display uppercase-track text-xs px-8 py-3 border border-[var(--color-kj-text)] hover:bg-[var(--color-kj-text)] hover:text-[var(--color-kj-bg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isNew ? createSubmitLabel : saving ? "Saving…" : "Save changes"}
            </button>
            {!isNew && status !== "archived" && (
              <button
                type="button"
                onClick={handleArchive}
                disabled={archiveMutation.isPending}
                className="text-xs uppercase-track text-[var(--color-kj-text-light)] hover:text-[var(--color-kj-text)] disabled:opacity-50 cursor-pointer"
              >
                {archiveMutation.isPending ? "Archiving…" : "Archive"}
              </button>
            )}
          </div>

          {(createMutation.isError || updateMutation.isError) && (
            <p className="text-xs uppercase-track text-[var(--color-kj-danger)]">
              Save failed.
            </p>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl uppercase-track">Images</h2>
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              {isNew ? staged.length : (existing?.Images.length ?? 0)} / {MAX_IMAGES}
            </span>
          </div>

          {isNew ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                {staged.map((s) => (
                  <div
                    key={s.key}
                    className="relative border border-[var(--color-kj-border)] group"
                  >
                    <img
                      src={s.previewUrl}
                      alt={s.file.name}
                      className="w-full aspect-square object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeStaged(s.key)}
                      className="absolute top-2 right-2 text-[10px] uppercase-track bg-[var(--color-kj-bg)] border border-[var(--color-kj-border)] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {staged.length === 0 && (
                  <p className="col-span-2 text-sm italic text-[var(--color-kj-text-light)]">
                    At least one image is required. Pick some below — they'll upload when you click Create.
                  </p>
                )}
              </div>

              <div className="mt-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={staged.length >= MAX_IMAGES}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      addStagedFiles(e.target.files);
                    }
                  }}
                  className="block w-full text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {staged.length >= MAX_IMAGES && (
                  <p className="mt-2 text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
                    Maximum of {MAX_IMAGES} images reached.
                  </p>
                )}
                {staged.length > 0 && staged.length < MAX_IMAGES && (
                  <p className="mt-2 text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
                    {staged.length} image{staged.length === 1 ? "" : "s"} ready to upload on create · {MAX_IMAGES - staged.length} more allowed
                  </p>
                )}
                {uploadError && (
                  <p className="mt-2 text-xs uppercase-track text-[var(--color-kj-danger)]">
                    {uploadError}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                {existing!.Images.map((img) => {
                  const isOnly = existing!.Images.length === 1;
                  const canRemove = !isOnly || existing!.Status === "archived";
                  return (
                    <div
                      key={imageId(img.ID)}
                      className="relative border border-[var(--color-kj-border)] group"
                    >
                      <img src={img.URL} alt="" className="w-full aspect-square object-cover" />
                      <button
                        type="button"
                        disabled={!canRemove}
                        title={!canRemove ? "Upload another image first — each piece must keep at least one." : undefined}
                        onClick={() => {
                          if (!canRemove) return;
                          if (confirm("Delete this image?")) {
                            deleteImageMutation.mutate(imageId(img.ID));
                          }
                        }}
                        className="absolute top-2 right-2 text-[10px] uppercase-track bg-[var(--color-kj-bg)] border border-[var(--color-kj-border)] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                {existing!.Images.length === 0 && (
                  <p className="col-span-2 text-sm italic text-[var(--color-kj-text-light)]">
                    No images yet.
                  </p>
                )}
              </div>

              <div className="mt-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={(existing?.Images.length ?? 0) >= MAX_IMAGES}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      editUploadMutation.mutate(e.target.files);
                    }
                  }}
                  className="block w-full text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {(existing?.Images.length ?? 0) >= MAX_IMAGES && (
                  <p className="mt-2 text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
                    Maximum of {MAX_IMAGES} images reached. Remove one before uploading more.
                  </p>
                )}
                {editUploadMutation.isPending && (
                  <p className="mt-2 text-xs uppercase-track text-[var(--color-kj-text-light)]">
                    Uploading…
                  </p>
                )}
                {uploadError && (
                  <p className="mt-2 text-xs uppercase-track text-[var(--color-kj-danger)]">
                    {uploadError}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default AdminArtworkEditorPage;
